/**
 * What the inbox shows: one finished row per notification, the four counters,
 * and where each row's button goes.
 *
 * Everybody with a workspace uses this screen, and its question is "what wants
 * something from me, and what has changed under me?" The words live in
 * `notifications.constants.ts`; the loading, the tab and the read-marking live
 * in `useNotificationsController.ts`. This file owns the decisions in between,
 * chiefly `hrefFor` — the whole of the routing, which is why the API returns a
 * target and never a URL.
 *
 * "Answered" is a state the model derives, not one the feed sends: an ask whose
 * `awaiting` has gone false changes its chip and its button but stays in its
 * tab and stays unread. Reading and answering are different acts.
 */

import { assertNever, isAsk } from "@ondo/shared";
import type {
  NotificationEntry,
  NotificationTarget,
  NotificationType,
  WorkspaceRef,
} from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { verificationsPath, workPath, workspacePath } from "../routes/routes.model.ts";
import {
  ACTIONS,
  CHIPS,
  DETAILS,
  EMPTY_COPY,
  NOTIFICATIONS_COPY,
  RESOLVED,
  SUBJECT_PREFIXES,
  TAB_OF,
  TITLES,
} from "./notifications.constants.ts";
import type { NotificationTab, TileId, Tone } from "./notifications.constants.ts";

/**
 * One row, with every decision already made.
 *
 * Every string here is finished — the view does no formatting, no pluralising
 * and no date arithmetic, which is what keeps the tests on this file rather than
 * on a rendered DOM.
 */
export interface NotificationRow {
  id: string;
  tab: Exclude<NotificationTab, "all">;
  chip: { label: string; tone: Tone };
  title: string;
  detail: string;
  /** "Work: Fat Car", or null when the record has no name yet. */
  subject: string | null;
  /** "From: Studio Erwin Wurm", or null when nobody is recorded as having sent it. */
  from: string | null;
  /** "7 Mar · 14:30". Paired with `isoTime` for the `<time datetime>`. */
  when: string;
  isoTime: string;
  action: { label: string; href: string };
  awaiting: boolean;
  read: boolean;
}

export interface Tile {
  id: TileId;
  label: string;
  count: number;
}

/**
 * Where a row's action goes.
 *
 * The whole of the routing decision, in one place, and the reason the API
 * returns a target rather than a URL: paths are this app's business and it
 * already owns every other one. A `verification-request` opens the Verifications
 * screen ON that request; an answered one opens the WORK, because the asker's
 * own request is not in the inbox that screen reads.
 */
export function hrefFor(
  workspace: WorkspaceRef,
  target: NotificationTarget,
  type?: NotificationType,
): string {
  // THE ONE PLACE THE TYPE OVERRULES THE TARGET, and it has to.
  //
  // A settlement notification targets `work:<id>` on purpose: the payload
  // discloses no settlement id and no figure, because those are handed to two
  // named parties by one narrow definer and a feed payload is where such a
  // decision goes to die (see `notification.events.ts`). The work is simply
  // somewhere both parties can be sent.
  //
  // But "somewhere both parties can be sent" is not where the reader needs to
  // go. `settlement.due` says money is owed and `settlement.remitted` asks the
  // artist to confirm or dispute — and the controls for both live on the
  // settlements screen, not on the work record. Routing by target alone sent a
  // notification labelled "View settlement" to a page with no settlement on it.
  //
  // Both portals name that destination `settlements`, so one path serves the
  // gallery reading what it is owed and the artist answering what it was paid.
  if (
    type === "settlement.due" ||
    type === "settlement.remitted" ||
    type === "settlement.answered"
  ) {
    return workspacePath(workspace, "settlements");
  }

  // The same shape for an offer of title. It targets the work so the buyer can
  // look at the picture, and the ANSWER lives on "Offered to you". Now that the
  // row is an ask, the button on it has to go where Accept is.
  if (type === "transfer.offered") {
    return workspacePath(workspace, "offers");
  }

  // And the same shape again for a loan PROPOSAL. It targets the work so the
  // borrower can look at the picture before deciding — which is what
  // `ondo.loan_proposed_work_ids()` was added for — and Accept and Decline live
  // on "Loans to you".
  //
  // `loan.answered` and `loan.ended` are NOT here, and the asymmetry is the
  // point: those reach the LENDER, whose side of a loan is the estate's own
  // rail, and `loan.ended` revokes the borrower's grant in the same statement
  // that writes it, so there is nothing for them to open at all. Both portals
  // that can borrow name this destination `borrowed`.
  if (type === "loan.proposed") {
    return workspacePath(workspace, "borrowed");
  }

  switch (target.kind) {
    case "verification-request":
      return verificationsPath(workspace, { request: target.requestId });
    case "affiliation-claim":
      return verificationsPath(workspace, { claim: target.claimId });
    // ONE TARGET, TWO DESTINATIONS, decided by which workspace is open rather
    // than by the notification's type. Both sides of a representation request
    // render the same row, and each workspace has exactly one screen that shows
    // it: the agent's own list of what they asked for, and everybody else's
    // third inbox tab. Reading it off the workspace keeps the routing decision
    // where every other path in this app is decided.
    case "representation-request":
      return workspace.role === "agent"
        ? workspacePath(workspace, "request-representation")
        : verificationsPath(workspace, { representation: target.requestId });
    case "provenance-entry":
      return workPath(workspace, target.workId, { entry: target.entryId });
    case "work":
      return workPath(workspace, target.workId);
    case "collection":
      return workspacePath(workspace, "collection");
    default:
      return assertNever(target);
  }
}

export function toRow(
  entry: NotificationEntry,
  workspace: WorkspaceRef,
): NotificationRow {
  const prefix = SUBJECT_PREFIXES[entry.type];

  /**
   * An ask that has been answered, which is a different row from an open one.
   *
   * `isAsk` and not `!entry.awaiting` alone: news is never awaiting, and a
   * collection somebody shared with you must not be labelled ANSWERED because
   * there was nothing to answer.
   */
  const settled = isAsk(entry.type) && !entry.awaiting;

  return {
    id: entry.id,
    tab: TAB_OF[entry.type],
    chip: settled ? RESOLVED.chip : CHIPS[entry.type],
    title: TITLES[entry.type],
    detail: settled
      ? entry.type === "affiliation.claimed"
        ? RESOLVED.detail.affiliation
        : RESOLVED.detail.verification
      : DETAILS[entry.type],
    // Null rather than "Work: —". A registry that does not know a work's title
    // yet should say nothing, not print a gap where a name goes.
    subject:
      prefix && entry.subjectLabel ? `${prefix}: ${entry.subjectLabel}` : null,
    from: entry.actor
      ? `${NOTIFICATIONS_COPY.from}: ${entry.actor.displayName}`
      : null,
    when: when(entry.createdAt),
    isoTime: entry.createdAt,
    action: {
      label: settled ? RESOLVED.action : ACTIONS[entry.type],
      href: hrefFor(workspace, entry.target, entry.type),
    },
    awaiting: entry.awaiting,
    read: entry.read,
  };
}

/** The rows a tab shows. `all` is every one of them, newest first as the API returns them. */
export function inTab(rows: NotificationRow[], tab: NotificationTab): NotificationRow[] {
  return tab === "all" ? rows : rows.filter((row) => row.tab === tab);
}

/**
 * The four counters.
 *
 * Three of them count what is still WAITING and the fourth counts what is
 * UNREAD, and the difference is the point: answering something in the
 * Verifications screen empties the first three without touching the fourth, and
 * reading the inbox empties the fourth without answering anything. A single
 * number pretending to be both would let clearing an inbox look like clearing
 * the work.
 */
export function tiles(rows: NotificationRow[], unread: number): Tile[] {
  const waiting = rows.filter((row) => row.awaiting);

  return [
    {
      id: "answer",
      label: "TO ANSWER",
      count: waiting.filter((row) => row.tab === "asks").length,
    },
    {
      id: "passed",
      label: "PASSED TO YOU",
      // By tab and chip together: a referral that has been answered wears the
      // ANSWERED chip, so counting the chip alone is what keeps this to the ones
      // still open. `waiting` has already excluded them, and this is belt and
      // braces on a count somebody acts on.
      count: waiting.filter(
        (row) =>
          row.tab === "asks" &&
          row.chip.label === CHIPS["verification.referred"].label,
      ).length,
    },
    {
      id: "affiliations",
      label: "AFFILIATION CLAIMS",
      count: waiting.filter((row) => row.tab === "affiliations").length,
    },
    { id: "unread", label: "UNREAD", count: unread },
  ];
}

/** "7 pending actions" — the header's subtitle, or nothing at all when none are. */
export function pending(rows: NotificationRow[]): string {
  const waiting = rows.filter((row) => row.awaiting).length;
  if (waiting === 0) return "";

  return `${waiting} pending ${waiting === 1 ? "action" : "actions"}`;
}

/**
 * Which emptiness this is.
 *
 * Two different sentences, because they call for different things: an inbox
 * nobody has written to needs explaining, and a tab with nothing under it just
 * needs pointing back at All.
 */
export function emptyFor(input: {
  total: number;
  shown: number;
}): { lead: string; detail: string } | null {
  if (input.shown > 0) return null;
  return input.total === 0 ? EMPTY_COPY.never : EMPTY_COPY.filtered;
}

/**
 * Whether a row's action should also mark it read.
 *
 * Only if it is not already, so a reader clicking through a row twice does not
 * send a second write that changes nothing.
 */
export function toMarkRead(rows: NotificationRow[], id: string): string[] {
  return rows.some((row) => row.id === id && !row.read) ? [id] : [];
}

export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong loading your notifications.";
}

/** "7 Mar · 14:30", in the reader's locale. */
function when(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  const date = at.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  const time = at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${date} · ${time}`;
}
